/**
 * The module edit lease the editor holds, and the timer that keeps it alive.
 *
 * Lifted out of `useModuleEditor` so the editor reads as the write path it is:
 * the lease answers to nothing else there beyond being taken, renewed, lost
 * and released.
 *
 * @module components/studio/module-lease-renewal
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import type { ProgrammePlatformClient } from "@/platform";
import { isLeaseConflict, isTransientFailure } from "@/platform/editor-sync-reducer";
import type { EditLease } from "@/server/types";

/** How often a held lease is renewed. Half the server's own window. */
export const LEASE_RENEWAL_MS = 30_000;

export interface ModuleLeaseState {
  /** The lease held, or null. The ref is what the timer and cleanup read. */
  lease: EditLease | null;
  leaseRef: React.RefObject<EditLease | null>;
  leaseError: Error | null;
  /** Adopt a lease just taken or renewed, ref and state together. */
  hold: (lease: EditLease) => void;
  /** Give it up with a reason, ref and state together. */
  lose: (error: Error) => void;
  /** Give it up with no reason, for a lease deliberately released. */
  release: () => void;
  /**
   * Take the lease unless one is held, at most one attempt at a time. A refused
   * attempt is not final: the holder may have gone, or a laptop slept past its renewal.
   */
  acquire: () => Promise<void>;
  /** True while an attempt is out, so the control that asked can say so. */
  acquiring: boolean;
  setLeaseError: (error: Error | null) => void;
  /** Returned to its builder, so no lease is taken until the reader asks. */
  handedBack: boolean;
  /** Mark the module returned, or not. A lease taken clears it. */
  handBack: (returned: boolean) => void;
}

/**
 * @param enabled - Whether leases are on at all. They are off outside
 * production, where a renewal would refuse against a stand-in id.
 */
export function useModuleLease(
  client: ProgrammePlatformClient,
  clientId: string,
  enabled: boolean,
  programmeId: string,
  moduleId: string,
): ModuleLeaseState {
  const [lease, setLease] = useState<EditLease | null>(null);
  const [leaseError, setLeaseError] = useState<Error | null>(null);
  const leaseRef = useRef<EditLease | null>(null);
  const [acquiring, setAcquiring] = useState(false);
  const [handedBack, setHandedBack] = useState(false);
  const handedBackRef = useRef(false);
  // A lease to ask for once a tick or the connection allows, after a lapse or a failed ask.
  const owedRef = useRef(false);
  const attemptRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const hold = useCallback((next: EditLease) => {
    leaseRef.current = next;
    owedRef.current = false;
    setLease(next);
  }, []);

  const handBack = useCallback((returned: boolean) => {
    handedBackRef.current = returned;
    if (returned) {
      owedRef.current = false;
    }
    setHandedBack(returned);
  }, []);

  const release = useCallback(() => {
    leaseRef.current = null;
    owedRef.current = false;
    setLease(null);
  }, []);

  const lose = useCallback(
    (error: Error) => {
      release();
      setLeaseError(error);
    },
    [release],
  );

  const attempt = useCallback(
    (quiet: boolean): Promise<void> => {
      // A ref as well as the state: two presses in one tick read the same flag,
      // and each extra acquire orphans the one before it, which then holds the
      // module against everybody until it expires.
      if (attemptRef.current || leaseRef.current) {
        return Promise.resolve();
      }
      attemptRef.current = true;
      setAcquiring(true);
      return client
        .acquireEditLease({ programmeId, moduleId, clientId })
        .then((next) => {
          // The editor's cleanup has already released what it held, so a grant
          // landing after it would hold the module until the lease expired. A
          // quiet ask is likewise moot once a release or hand-back cleared the debt.
          if (!mountedRef.current || (quiet && !owedRef.current)) {
            client.releaseEditLease({ leaseId: next.id, clientId }).catch(() => undefined);
            return;
          }
          hold(next);
          // Only once held: the control that asked stays on screen, busy.
          handBack(false);
          setLeaseError(null);
        })
        .catch((cause: unknown) => {
          // The server's reason names who holds the module; keep it.
          const error =
            cause instanceof Error
              ? cause
              : new Error("Unable to acquire the module editing lease");
          // Still owed only if no release or hand-back cleared it meanwhile.
          if (quiet && isTransientFailure(error)) {
            return;
          }
          owedRef.current = false;
          setLeaseError(error);
        })
        .finally(() => {
          attemptRef.current = false;
          setAcquiring(false);
        });
    },
    [client, clientId, hold, moduleId, programmeId, handBack],
  );

  const acquire = useCallback((): Promise<void> => attempt(false), [attempt]);

  useEffect(() => {
    const askOwed = (): void => {
      if (owedRef.current && !leaseRef.current) {
        attempt(true).catch(() => undefined);
      }
    };
    const renew = (): void => {
      const held = leaseRef.current;
      if (!enabled || !window.navigator.onLine) {
        return;
      }
      if (!held) {
        askOwed();
        return;
      }
      // A renewal still out when the lease is released or replaced answers for
      // a lease this editor no longer holds.
      const current = () => leaseRef.current?.id === held.id;
      client
        .renewEditLease({ leaseId: held.id, clientId })
        .then((renewed) => {
          if (current()) {
            hold(renewed);
          }
        })
        .catch((cause: unknown) => {
          if (!current()) {
            return;
          }
          const error =
            cause instanceof Error ? cause : new Error("The module editing lease was lost");
          if (isTransientFailure(error)) {
            return;
          }
          if (isLeaseConflict(error)) {
            lose(error);
            return;
          }
          release();
          owedRef.current = !handedBackRef.current;
          askOwed();
        });
    };
    const interval = setInterval(renew, LEASE_RENEWAL_MS);
    window.addEventListener("online", renew);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", renew);
    };
  }, [attempt, client, clientId, enabled, hold, lose, release]);

  return {
    lease,
    leaseRef,
    leaseError,
    hold,
    lose,
    release,
    acquire,
    acquiring,
    setLeaseError,
    handedBack,
    handBack,
  };
}
